import { StylesProps } from '../../../theme/jss-types';
import { summaryStepStyles } from '../summary-step.styles';
import GenericHoc from 'components/common/generic-hoc';
import { HocOptions } from 'components/common/generic-hoc.types';
import * as React from 'react';
import { CurrentPage } from '../create-consignment.types';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { get } from 'lodash';
import { EditOutlined } from '@ant-design/icons';
import {
    Tooltip, Collapse, Col, Row,
    Divider,
} from 'antd';
import i18n from 'i18n';
import { snakeToPretty } from 'library/lodash-helper';
import { formatDateTime } from 'components/consignments/consignments.helper';
import useIsMobile from 'hooks/useIsMobile';
import { formFields } from '../create-modal.constants';

const {
    ReferenceNumber,
    InvoiceNumber,
    InvoiceStatus,
    InvoiceDate,
    InvoiceAmount,
    EwbNumber,
    SenderGstin,
    ReceiverGstin,
    EwbCreationTime,
    EwbExpiryTime,
    TransporterGstin,
    TransporterName,
    ProofImageList,
    DeliveryDate,
    DeliveryTimeSlot,
    DeliveryDateAndTimeslot,
} = formFields;

const customLabels = {
    consigneeCompanyName: 'consignee_company_name',
    receiverVATNumber: 'receiver_vat_number',
    destinationType: 'consignee_type',
    consigneeCode: 'consignee_code',
    isCOD: 'is_cod',
    serviceType: 'service_type',
    srcAddress: 'Source Address',
    dstAddress: 'Delivery Address',
    boxDetails: 'Box Details',
    billAddress: 'Billing Address',
    dangerousGoodUnNumber: 'UN Number',
    dangerousGoodCategoryPackingType: 'Packing Type',
    documentDetails: 'Document Details',
    invoiceReferenceNumber: 'Invoice Reference Number',
};

interface SummaryProps extends StylesProps<ReturnType<typeof summaryStepStyles>> {
    formData: any;
    handleTabChange: (tab: CurrentPage) => void;
    sections: any;
    showDeliveryType: boolean;
    isCopApplicable: boolean;
    showEwb: boolean;
    pickupScheduleCreation: boolean;
    pieceLayout: any;
    dgLayout: any;
    customFieldsList : [];
    invoiceLayout: any;
}

const SummaryStep = (props: SummaryProps) => {
    const {
        classes,
        formData,
        handleTabChange,
        sections,
        showDeliveryType,
        isCopApplicable,
        showEwb,
        pickupScheduleCreation,
        pieceLayout,
        dgLayout,
        customFieldsList,
        invoiceLayout,
    } = props;

    const isMobile = useIsMobile();

    const hideFields = {
        typeOfDelivery: !showDeliveryType,
        retailTransactionType: !isCopApplicable,
        cashonpickup: !isCopApplicable,
        ewbNumber: !showEwb,
        ewbDate: !showEwb,
        kiranaHubCode: !showEwb,
        pickupSchedule: !pickupScheduleCreation,
    };
    const { t } = useTranslation();
    const [data, setData] = React.useState<any>(formData);
    React.useEffect(() => {
        function unflattenObject(flatObj: any) {
            const unflatten = (obj: any) => {
                const result = {};
                const objKeys = Object.keys(obj);
                objKeys.forEach((key) => {
                    const keys = key.split('.');
                    let nestedObj: any = result;
                    for (let i = 0; i < keys.length - 1; i += 1) {
                        const nestedKey = keys[i];
                        if (!(nestedKey in nestedObj)) {
                            nestedObj[nestedKey] = {};
                        }
                        nestedObj = nestedObj[nestedKey];
                    }
                    nestedObj[keys[keys.length - 1]] = obj[key];
                });
                return result;
            };

            return unflatten(flatObj);
        }
        setData(unflattenObject(formData));
    }, [formData]);

    const renderAddress = (addressType: any) => {
        const address = data[addressType];
        return (
            <>
                <div
                    key={`${addressType}`}
                    style={{
                        width: '50%',
                        paddingRight: '15px',
                        marginBottom: '12px',
                    }}
                >
                    <div key={`${addressType}-heading`} style={{ fontSize: '12px', color: '#666' }}>
                        {t(customLabels[addressType as keyof typeof customLabels] || addressType)}
                    </div>
                    {
                        address ? (
                            <>
                                <div style={{ fontWeight: 600, fontSize: '13px' }}>
                                    {address.name || ''}
                                </div>
                                <div style={{ fontWeight: 600, fontSize: '13px' }}>
                                    {`${address.addressLine1
                                        ? `${address.addressLine1}, ` : ''} ${address.addressLine2
                                        ? `${address.addressLine2}, ` : ''} ${address.addressLine3
                                        ? `${address.addressLine3}, ` : ''} ${address.addressLine4
                                        ? `${address.addressLine4}, ` : ''} ${address.addressLine5 || ''}`}
                                </div>
                                <div style={{ fontWeight: 600, fontSize: '13px' }}>
                                    {`${address.cityName
                                        ? `${address.cityName} - ` : ''} ${address.pincode || ''}`}
                                    {`${address.stateName
                                        ? `${address.stateName}, ` : ''} ${address.countryName || ''}`}
                                </div>
                                {address.district ? (
                                    <div style={{ fontWeight: 600, fontSize: '13px' }}>
                                        {address.district}
                                    </div>
                                ) : null}
                                <div style={{ fontWeight: 600, fontSize: '13px' }}>
                                    {address.phone || ''}
                                </div>
                                {address.alternatePhone ? (
                                    <div style={{ fontWeight: 600, fontSize: '13px' }}>
                                        {address.alternatePhone}
                                    </div>
                                ) : null}
                                {address.alternatePhone2 ? (
                                    <div style={{ fontWeight: 600, fontSize: '13px' }}>
                                        {address.alternatePhone2}
                                    </div>
                                ) : null}
                                {address.timeSlotList?.length ? (
                                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                        {t('time_slot')}
                                    </div>
                                ) : null}
                                {address.timeSlotList?.map((slot: string[]) => (
                                    <div style={{ fontWeight: 600, fontSize: '13px' }}>
                                        {`${slot[0]} - ${slot[1]}`}
                                    </div>
                                ))}
                                {(address.latitude || address.longitude) ? (
                                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                        {`${t('latitude')}: ${address.latitude ?? '-'}, `
                                        + `${t('longitude')}: ${address.longitude ?? '-'}`}
                                    </div>
                                ) : null}
                            </>
                        ) : '-'
                    }
                </div>
            </>
        );
    };

    const renderConsignorDetails = () => {
        return renderAddress('srcAddress');
    };

    const renderConsigneeDetails = () => {
        const { redirectionAddress } = data;
        return (
            <>
                {renderAddress('dstAddress')}
                {
                    redirectionAddress ? renderAddress('redirectionAddress') : null
                }
            </>
        );
    };

    const renderAddressField = () => {
        return (
            <>
                {renderConsignorDetails()}
                {renderConsigneeDetails()}
            </>
        );
    };

    const renderDimensions = (d = data, isPieceLevel: boolean) => {
        const obj = { ...d };
        if (isPieceLevel) {
            obj.dimensions = obj.dimensions || {
                length: obj.length,
                width: obj.width,
                height: obj.height,
            };
            obj.dimensionUnit = obj.unit;
        }
        const fieldWidth = isMobile ? '50%' : '33%';
        return (
            <>
                <div key="dimensions.length" style={{ width: fieldWidth, paddingRight: '12px', marginBottom: '12px' }}>
                    <div key="dimensions.length-heading" style={{ fontSize: '12px', color: '#666' }}>
                        {t(customLabels['length' as keyof typeof customLabels] || 'length')}
                    </div>
                    <div style={{ fontWeight: 600, fontSize: '13px' }}>
                        {obj.dimensions?.length ? `${obj.dimensions.length} ${obj.dimensionUnit}` : '-'}
                    </div>
                </div>
                <div key="dimensions.width" style={{ width: fieldWidth, paddingRight: '12px', marginBottom: '12px' }}>
                    <div key="dimensions.width-heading" style={{ fontSize: '12px', color: '#666' }}>
                        {t(customLabels['width' as keyof typeof customLabels] || 'width')}
                    </div>
                    <div style={{ fontWeight: 600, fontSize: '13px' }}>
                        {obj.dimensions?.width ? `${obj.dimensions.width} ${obj.dimensionUnit}` : '-'}
                    </div>
                </div>
                <div key="dimensions.height" style={{ width: fieldWidth, paddingRight: '12px', marginBottom: '12px' }}>
                    <div key="dimensions.height-heading" style={{ fontSize: '12px', color: '#666' }}>
                        {t(customLabels['height' as keyof typeof customLabels] || 'height')}
                    </div>
                    <div style={{ fontWeight: 600, fontSize: '13px' }}>
                        {obj.dimensions?.height ? `${obj.dimensions.height} ${obj.dimensionUnit}` : '-'}
                    </div>
                </div>
            </>
        );
    };

    const renderDimensionsV1 = () => {
        const pieces = Object.keys(data.piecesDetail)?.map((key) => {
            return {
                ...data.piecesDetail[key],
                id: key,
            };
        });
        const fieldWidth = isMobile ? '50%' : '25%';
        if (data.courierType === 'document' || data.courierType === 'envelope') {
            return (
                <>
                    <div key="weight" style={{ width: fieldWidth, paddingRight: '12px', marginBottom: '12px' }}>
                        <div key="weight-heading" style={{ fontSize: '12px', color: '#666' }}>
                            {t(customLabels['weight' as keyof typeof customLabels] || 'weight')}
                        </div>
                        <div style={{ fontWeight: 600, fontSize: '13px' }}>
                            {data.weight ? `${data.weight} ${data.weightUnit}` : '-'}
                        </div>
                    </div>
                    <div key="description" style={{ width: fieldWidth, paddingRight: '12px', marginBottom: '12px' }}>
                        <div key="description-heading" style={{ fontSize: '12px', color: '#666' }}>
                            {t(customLabels['description' as keyof typeof customLabels] || 'description')}
                        </div>
                        <div style={{ fontWeight: 600, fontSize: '13px' }}>
                            {data.description || '-'}
                        </div>
                    </div>
                </>
            );
        }
        return (
            <>
                <div key="quantity" style={{ width: fieldWidth, paddingRight: '12px', marginBottom: '12px' }}>
                    <div key="quantity-heading" style={{ fontSize: '12px', color: '#666' }}>
                        {t(customLabels['quantity' as keyof typeof customLabels] || 'quantity')}
                    </div>
                    <div style={{ fontWeight: 600, fontSize: '13px' }}>
                        {pieces?.length || '-'}
                    </div>
                </div>
                <div key="weight" style={{ width: fieldWidth, paddingRight: '12px', marginBottom: '12px' }}>
                    <div key="weight-heading" style={{ fontSize: '12px', color: '#666' }}>
                        {t(customLabels['weight' as keyof typeof customLabels] || 'weight')}
                    </div>
                    <div style={{ fontWeight: 600, fontSize: '13px' }}>
                        {data.weight ? `${data.weight} ${data.weightUnit}` : '-'}
                    </div>
                </div>
                <div key="description" style={{ width: fieldWidth, paddingRight: '12px', marginBottom: '12px' }}>
                    <div key="description-heading" style={{ fontSize: '12px', color: '#666' }}>
                        {t(customLabels['description' as keyof typeof customLabels] || 'description')}
                    </div>
                    <div style={{ fontWeight: 600, fontSize: '13px' }}>
                        {data.description || '-'}
                    </div>
                </div>
                {
                    pieces.map((piece: any) => {
                        return (
                            <div
                                key={piece.id}
                                style={{ display: 'flex', width: '100%', flexWrap: 'wrap' }}
                            >
                                <div
                                    key={`${piece.id}-length`}
                                    style={{
                                        width: fieldWidth,
                                        paddingRight: '12px',
                                        marginBottom: '12px',
                                    }}
                                >
                                    <div
                                        key={`${piece.id}-heading`}
                                        style={{ fontSize: '12px', color: '#666' }}
                                    >
                                        {t(customLabels['length' as keyof typeof customLabels] || 'length')}
                                    </div>
                                    <div style={{ fontWeight: 600, fontSize: '13px' }}>
                                        {piece.length ? `${piece.length} ${piece.dimensionsUnit}` : '-'}
                                    </div>
                                </div>
                                <div
                                    key={`${piece.id}-width`}
                                    style={{
                                        width: fieldWidth,
                                        paddingRight: '12px',
                                        marginBottom: '12px',
                                    }}
                                >
                                    <div
                                        key={`${piece.id}-heading`}
                                        style={{ fontSize: '12px', color: '#666' }}
                                    >
                                        {t(customLabels['width' as keyof typeof customLabels] || 'width')}
                                    </div>
                                    <div style={{ fontWeight: 600, fontSize: '13px' }}>
                                        {piece.width ? `${piece.width} ${piece.dimensionsUnit}` : '-'}
                                    </div>
                                </div>
                                <div
                                    key={`${piece.id}-height`}
                                    style={{
                                        width: fieldWidth,
                                        paddingRight: '12px',
                                        marginBottom: '12px',
                                    }}
                                >
                                    <div
                                        key={`${piece.id}-heading`}
                                        style={{ fontSize: '12px', color: '#666' }}
                                    >
                                        {t(customLabels['height' as keyof typeof customLabels] || 'height')}
                                    </div>
                                    <div style={{ fontWeight: 600, fontSize: '13px' }}>
                                        {piece.height ? `${piece.height} ${piece.dimensionsUnit}` : '-'}
                                    </div>
                                </div>
                                <div
                                    key={`${piece.id}-weight`}
                                    style={{
                                        width: fieldWidth,
                                        paddingRight: '12px',
                                        marginBottom: '12px',
                                    }}
                                >
                                    <div
                                        key={`${piece.id}-heading`}
                                        style={{ fontSize: '12px', color: '#666' }}
                                    >
                                        {t(customLabels['weight' as keyof typeof customLabels] || 'weight')}
                                    </div>
                                    <div style={{ fontWeight: 600, fontSize: '13px' }}>
                                        {piece.weight ? `${piece.weight} ${piece.weightUnit}` : '-'}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                }
            </>
        );
    };

    const renderPickupSchedule = () => {
        const fieldWidth = isMobile ? '50%' : '25%';
        return (
            <div
                key="pickupSchedule"
                style={{ width: fieldWidth, paddingRight: '12px', marginBottom: '12px' }}
            >
                <div key="pickupSchedule-heading" style={{ fontSize: '12px', color: '#666' }}>
                    {t(customLabels['pickupSchedule' as keyof typeof customLabels] || 'pickupSchedule')}
                </div>
                <div style={{ fontWeight: 600, fontSize: '13px' }}>
                    {moment(data.pickupDate || data.pickupSchedule).format('YYYY-MM-DD') || '-'}
                </div>
            </div>
        );
    };

    const renderTaxDetailsField = (field: any, _data: any = {}) => {
        const transformKey = (key: any) => {
            if (key.startsWith('taxDetails')) {
                const newKey = key.replace('taxDetails', '');
                return newKey.charAt(0).toLowerCase() + newKey.slice(1);
            }
            return key;
        };
        const newKey = transformKey(field);
        return (
            <div
                key={field}
                style={{
                    width: isMobile ? '50%' : '25%',
                    paddingRight: '12px',
                    marginBottom: '12px',
                }}
            >
                <div key={`${field}-heading`} style={{ fontSize: '12px', color: '#666' }}>
                    {t(field)}
                </div>
                <div key={`${field}-value`} style={{ fontWeight: 600, fontSize: '13px' }}>
                    {t(_data[newKey]) || '-'}
                </div>
            </div>
        );
    };

    const renderPieceField = (idx: any, key: string | undefined) => {
        if (!key) return null;
        const dataPair = {
            label: key,
            value: data.piecesDetail[idx][key],
        };
        switch (key) {
            case 'length':
            case 'width':
            case 'height':
                dataPair.value = dataPair.value
                    ? `${dataPair.value}  ${data.piecesDetail[idx]?.dimensionsUnit || 'cm'}` : '-';
                break;
            case 'declaredValue':
                dataPair.value = dataPair.value
                    ? `${dataPair.value}  ${data.declaredCurrency || ''}` : '-';
                break;
            case 'codAmount':
                dataPair.value = dataPair.value
                    ? `${dataPair.value}  ${data.currency || ''}` : '-';
                break;
            case 'weight':
                dataPair.value = dataPair.value ? `${dataPair.value}  ${data.piecesDetail[idx]?.weightUnit || ''}` : '-';
                break;
            case 'reference_number': {
                const refNumber = data.piecesDetail[idx][key];
                dataPair.label = 'pieceReferenceNumber';
                dataPair.value = (
                    <Tooltip title={refNumber}>
                        {refNumber?.length > 12 ? `${refNumber.slice(0, 12)}...` : refNumber}
                    </Tooltip>
                );
                break;
            }
            case 'isKit':
                dataPair.value = dataPair.value ? 'Yes' : 'No';
                break;
            case 'manufacturingCountry':
                dataPair.value = data.piecesDetail[idx]?.manufacturingCountryName || '-';
                break;
            case 'dimensions':
                return renderDimensions(data.piecesDetail[idx], true);
            case 'taxDetailsTaxPercentage':
            case 'taxDetailsTotalTax':
            case 'taxDetailsCgst':
            case 'taxDetailsIgst':
            case 'taxDetailsSgst':
                return renderTaxDetailsField(key, data.piecesDetail[idx]);
            default:
                break;
        }
        return (
            <Col span={24} xs={12} md={12} xl={8} key={dataPair.label} className={classes.fieldGroup}>
                <div className={classes.fieldTitle}>
                    {i18n.exists(dataPair.label) ? t(dataPair.label) : snakeToPretty(dataPair.label)}
                </div>
                <div className={classes.fieldValue}>
                    {dataPair.value || '-'}
                </div>
            </Col>
        );
    };

    const renderInvoiceField = (idx: any, key: string | undefined) => {
        if (!key) return null;
        const dataPair = {
            label: key,
            value: data.invoicesDetail?.[idx]?.[key],
        };
        switch (key) {
            case InvoiceDate.key:
                dataPair.value = dataPair.value ? moment(dataPair.value).format('YYYY-MM-DD') : '-';
                break;
            case EwbCreationTime.key:
            case EwbExpiryTime.key:
                dataPair.value = dataPair.value ? formatDateTime(dataPair.value, 'datetime') : '-';
                break;
            case InvoiceAmount.key:
                dataPair.value = dataPair.value ? `${dataPair.value} ${data.currency || ''}` : '-';
                break;
            case ReferenceNumber.key: {
                const refNumber = data.invoicesDetail?.[idx]?.[key];
                dataPair.label = 'pieceReferenceNumber';
                dataPair.value = (
                    <Tooltip title={refNumber}>
                        <span className={classes.pieceReferenceNumberStyle}>
                            {refNumber}
                        </span>
                    </Tooltip>
                );
                break;
            }
            case ProofImageList.key: {
                const imageList = data.invoicesDetail?.[idx]?.[key];
                dataPair.value = `${imageList?.length || 0} image${imageList?.length !== 1 ? 's' : ''}`;
                break;
            }
            default:
                break;
        }
        return (
            <Col span={24} xs={12} md={8} xl={6} key={dataPair.label} className={classes.fieldGroup}>
                <div className={classes.fieldTitle}>
                    {i18n.exists(dataPair.label) ? t(dataPair.label) : snakeToPretty(dataPair.label)}
                </div>
                <div className={classes.fieldValue}>
                    {dataPair.value || '-'}
                </div>
            </Col>
        );
    };

    const renderDgField = (key: string, dgData: any) => {
        switch (key) {
            case 'dangerousGoodPackingGroup':
            case 'dangerousGoodCategory':
                return dgData[key] ? snakeToPretty(dgData[key]) : '-';
            default:
                return dgData[key] || '-';
        }
    };

    const renderDangerousGood = (isPieceLevel: boolean, dgData = data, idx = -1) => {
        const dgKeys = dgLayout.filter((key: any) => [
            'isDangerousGood',
            'dangerousGoodAccessible',
            'dangerousGoodUnNumber',
            'dangerousGoodName',
            'dangerousGoodDivision',
            'dangerousGoodPackingGroup',
            'dangerousGoodCategory',
            'dangerousGoodCategoryType',
            'dangerousGoodCategoryPackingInstruction',
            'dangerousGoodClass',
        ].includes(key));
        const fieldWidth = isMobile ? '50%' : '25%';
        return (
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    flexWrap: 'wrap',
                    rowGap: 12,
                }}
            >
                {
                    dgKeys.map((key: any) => {
                        const uniqueKey = `${idx}-${key}`;
                        return (
                            <div key={uniqueKey} style={{ width: fieldWidth, paddingRight: '12px', marginBottom: '12px' }}>
                                <div key={`${uniqueKey}-heading`} style={{ fontSize: '12px', color: '#666' }}>
                                    {t(customLabels[key as keyof typeof customLabels] || key)}
                                </div>
                                <div key={`${uniqueKey}-value`} style={{ fontWeight: 600, fontSize: '13px' }}>
                                    {renderDgField(key, dgData)}
                                </div>
                            </div>
                        );
                    })
                }
            </div>
        );
    };

    const renderPieceDetails = (piece: any, idx: number) => {
        const pieceKeys = pieceLayout.filter((key: any) => [
            'reference_number',
            'description',
            'length',
            'width',
            'height',
            'weight',
            'unit',
            'itemUnit',
            'quantity',
            'codAmount',
            'dimensions',
            'purchaseOrderNumber',
            'pieceProductCode',
            'declaredValue',
            'eccn',
            'isKit',
            'hsCode',
            'manufacturingCountry',
            'taxDetailsTotalTax',
            'taxDetailsTaxPercentage',
            'taxDetailsIgst',
            'taxDetailsSgst',
            'taxDetailsCgst',
            'taxDetailsRecieverGstin',
            'taxDetailsSenderGstin',
            'forwardReferenceNumber',
            'productCategory',
            'productFamily',
            'brandName',
        ].includes(key));
        return (
            <div style={{ width: '100%', marginBottom: 16 }}>
                <Collapse expandIconPosition="right" bordered={false} ghost>
                    <Collapse.Panel
                        className={classes.collapse}
                        key={piece.id}
                        header={`${data.is_international ? 'Box' : t('Piece')} ${idx + 1}`}
                    >
                        <Row>
                            {
                                pieceKeys.map((key: any) => renderPieceField(idx, key))
                            }
                            {
                                pieceLayout.includes('dangerousGood') ? renderDangerousGood(true, piece, idx) : null
                            }
                        </Row>
                    </Collapse.Panel>
                </Collapse>
            </div>
        );
    };


    const renderPieces = () => {
        return data?.piecesDetail?.map((piece: any, idx: number) => {
            return renderPieceDetails(piece, idx);
        });
    };

    const renderInvoiceDetails = (invoice: any, idx: number) => {
        const invoiceKeys = invoiceLayout.filter((key: any) => [
            ReferenceNumber.key,
            InvoiceNumber.key,
            InvoiceStatus.key,
            InvoiceDate.key,
            InvoiceAmount.key,
            EwbNumber.key,
            SenderGstin.key,
            ReceiverGstin.key,
            EwbCreationTime.key,
            EwbExpiryTime.key,
            TransporterGstin.key,
            TransporterName.key,
            ProofImageList.key,
        ].includes(key));
        return (
            <div style={{ width: '100%' }}>
                <Collapse expandIconPosition="right" bordered={false} ghost>
                    <Collapse.Panel
                        className={classes.collapse}
                        key={idx}
                        header={`Invoice ${idx + 1}`}
                    >
                        <Row>
                            {
                                invoiceKeys.map((key: any) => renderInvoiceField(idx, key))
                            }
                        </Row>
                    </Collapse.Panel>
                </Collapse>
            </div>
        );
    };

    const renderInvoices = () => {
        return data?.invoicesDetail?.map((invoice: any, idx: number) => {
            return renderInvoiceDetails(invoice, idx);
        });
    };

    const renderDeclaredValue = () => {
        const value = data.declaredValue || data.declaredPrice;
        const currency = data.declaredCurrency || '';
        const fieldWidth = isMobile ? '50%' : '25%';
        return (
            <div
                key="declaredValue"
                style={{ width: fieldWidth, paddingRight: '12px', marginBottom: '12px' }}
            >
                <div key="declaredValue-heading" style={{ fontSize: '12px', color: '#666' }}>
                    {t(customLabels['declaredValue' as keyof typeof customLabels] || 'declaredValue')}
                </div>
                <div style={{ fontWeight: 600, fontSize: '13px' }}>
                    {value ? `${value} ${currency}` : '-'}
                </div>
            </div>
        );
    };

    const renderPickupTime = () => {
        const { pickupTimeSlotStart, pickupTimeSlotEnd } = data;
        return (
            <div key="pickupTime" style={{ width: isMobile ? '50%' : '25%', paddingRight: '12px', marginBottom: '12px' }}>
                <div key="pickupTime-heading" style={{ fontSize: '12px', color: '#666' }}>
                    {t(customLabels['pickupTime' as keyof typeof customLabels] || 'pickupTime')}
                </div>
                <div style={{ fontWeight: 600, fontSize: '13px' }}>
                    {pickupTimeSlotStart && pickupTimeSlotEnd ? `${pickupTimeSlotStart} - ${pickupTimeSlotEnd}` : '-'}
                </div>
            </div>
        );
    };

    const renderDeliveryTimeslot = () => {
        const { deliveryTimeSlotStart, deliveryTimeSlotEnd } = data;
        return (
            <div
                key={DeliveryTimeSlot.key}
                style={{ width: isMobile ? '50%' : '25%', paddingRight: '12px', marginBottom: '12px' }}
            >
                <div key={`${DeliveryTimeSlot.key}-heading`} style={{ fontSize: '12px', color: '#666' }}>
                    {t(DeliveryTimeSlot.key)}
                </div>
                <div style={{ fontWeight: 600, fontSize: '13px' }}>
                    {deliveryTimeSlotStart && deliveryTimeSlotEnd
                        ? `${formatDateTime(deliveryTimeSlotStart, 'time')} -
                            ${formatDateTime(deliveryTimeSlotEnd, 'time')}` : '-'}
                </div>
            </div>
        );
    };

    const renderDeliveryDate = () => {
        const fieldWidth = isMobile ? '50%' : '25%';
        return (
            <div
                key={DeliveryDate.key}
                style={{ width: fieldWidth, paddingRight: '12px', marginBottom: '12px' }}
            >
                <div key={`${DeliveryDate.key}-heading`} style={{ fontSize: '12px', color: '#666' }}>
                    {t(DeliveryDate.key)}
                </div>
                <div style={{ fontWeight: 600, fontSize: '13px' }}>
                    {data.deliveryDate ? moment(data.deliveryDate).format('YYYY-MM-DD') : '-'}
                </div>
            </div>
        );
    };

    const renderCodAmount = () => {
        const value = data.codAmount;
        const currency = data.currency || '';
        return (
            <div key="codAmount" style={{ width: isMobile ? '50%' : '25%', paddingRight: '12px', marginBottom: '12px' }}>
                <div key="codAmount-heading" style={{ fontSize: '12px', color: '#666' }}>
                    {t(customLabels['codAmount' as keyof typeof customLabels] || 'codAmount')}
                </div>
                <div style={{ fontWeight: 600, fontSize: '13px' }}>
                    {value ? `${value} ${currency}` : '-'}
                </div>
            </div>
        );
    };

    const renderWeight = () => {
        const weightUnit = data.weightUnit || '';
        return (
            <div key="weight" style={{ width: isMobile ? '50%' : '25%', paddingRight: '12px', marginBottom: '12px' }}>
                <div key="weight-heading" style={{ fontSize: '12px', color: '#666' }}>
                    {t(customLabels['weight' as keyof typeof customLabels] || 'weight')}
                </div>
                <div style={{ fontWeight: 600, fontSize: '13px' }}>
                    {data.weight ? `${data.weight} ${weightUnit}` : '-'}
                </div>
            </div>
        );
    };

    const renderAccount = () => {
        return (
            <div key="childClient" style={{ width: isMobile ? '50%' : '25%', paddingRight: '12px', marginBottom: '12px' }}>
                <div key="childClient-heading" style={{ fontSize: '12px', color: '#666' }}>
                    {t(customLabels['childClient' as keyof typeof customLabels] || 'childClient')}
                </div>
                <div style={{ fontWeight: 600, fontSize: '13px' }}>
                    {data.childClient && data.childClient.split('+')[2] ? data.childClient.split('+')[2] : '-'}
                </div>
            </div>
        );
    };

    const renderManufacturingCountry = () => {
        const fieldWidth = isMobile ? '50%' : '25%';
        return (
            <div
                key="manufacturingCountry"
                style={{ width: fieldWidth, paddingRight: '12px', marginBottom: '12px' }}
            >
                <div key="manufacturingCountry-heading" style={{ fontSize: '12px', color: '#666' }}>
                    {t(customLabels['manufacturingCountry' as keyof typeof customLabels]
                        || 'manufacturingCountry')}
                </div>
                <div key="manufacturingCountry-value" style={{ fontWeight: 600, fontSize: '13px' }}>
                    {data.manufacturingCountryName || '-'}
                </div>
            </div>
        );
    };

    const renderDocumentDetails = () => {
        const documentDetails = data?.documentDetails || [];
        return (
            <div
                key="documentDetails"
                style={{ width: '50%', paddingRight: '12px', marginBottom: '12px' }}
            >
                <div key="documentDetails-heading" style={{ fontSize: '12px', color: '#666' }}>
                    {t(customLabels['documentDetails' as keyof typeof customLabels]
                        || 'documentDetails')}
                </div>
                <div style={{ fontWeight: 600, fontSize: '13px' }}>
                    {documentDetails?.map((doc: any) => {
                        const fileCount = doc?.new_files_uploaded?.length
                            || doc?.existing_document_details?.length || 0;
                        return (
                            <div key={`${doc?.document_type}-${fileCount}`}>
                                {snakeToPretty(doc?.document_type)}
                                {': '}
                                {fileCount}
                                {' file'}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderField = (field: any) => {
        if (hideFields[field as keyof typeof hideFields]) return null;
        switch (field) {
            case 'address':
                return renderAddressField();
            case 'consignorDetails':
                return renderConsignorDetails();
            case 'consigneeDetails':
                return renderConsigneeDetails();
            case 'srcAddress':
                return renderAddress('srcAddress');
            case 'dstAddress':
                return renderAddress('dstAddress');
            case 'redirectionAddress':
                return renderAddress('redirectionAddress');
            case 'alternateDelAddress1':
                return renderAddress('alternateDelAddress1');
            case 'alternateDelAddress2':
                return renderAddress('alternateDelAddress2');
            case 'billToDetails':
                return renderAddress('billAddress');
            case 'weight':
                return renderWeight();
            case 'dimensions':
                return renderDimensions(undefined, false);
            case 'dimensionsV1':
                return renderDimensionsV1();
            case 'pickupSchedule':
                return renderPickupSchedule();
            case 'declaredValue':
                return renderDeclaredValue();
            case 'codAmount':
                return renderCodAmount();
            case 'pickupTime':
                return renderPickupTime();
            case DeliveryTimeSlot.key:
                return renderDeliveryTimeslot();
            case DeliveryDate.key:
                return renderDeliveryDate();
            case DeliveryDateAndTimeslot.key:
                return (
                    <>
                        {renderDeliveryDate()}
                        {renderDeliveryTimeslot()}
                    </>
                );
            case 'childClient':
                return renderAccount();
            case 'dangerousGood':
                return renderDangerousGood(false);
            case 'manufacturingCountry':
                return renderManufacturingCountry();
            case 'piece':
                return null;
            case 'invoice':
                return null;
            case 'documentDetails':
                return renderDocumentDetails();
            case 'taxDetailsTaxPercentage':
            case 'taxDetailsTotalTax':
            case 'taxDetailsCgst':
            case 'taxDetailsIgst':
            case 'taxDetailsSgst':
                return renderTaxDetailsField(field, data);
            default: {
                const isCustomField = customFieldsList?.some((customField: any) => customField?.id === field);
                const customFieldObject: any = customFieldsList?.find(
                    (customField: any) => customField?.id === field,
                ) || { prettyName: '', dataType: '' };

                const customFieldLabel = isCustomField
                    ? customFieldObject?.prettyName
                    : '';
                const prettyName = !isCustomField
                    ? (customLabels[field as keyof typeof customLabels] || field)
                    : customFieldLabel;

                const isDateTimeField = isCustomField
                    && (customFieldObject?.dataType === 'datetime'
                    || customFieldObject?.dataType === 'date');
                const fieldValue = Array.isArray(data[field])
                    ? data[field].join(', ')
                    : t(data[field]) || '-';

                return (
                    <div
                        key={field}
                        style={{
                            width: isMobile ? '50%' : '25%',
                            paddingRight: '12px',
                            marginBottom: '12px',
                        }}
                    >
                        <div key={`${field}-heading`} style={{ fontSize: '12px', color: '#666' }}>
                            {t(prettyName)}
                        </div>
                        <div key={`${field}-value`} style={{ fontWeight: 600, fontSize: '13px' }}>
                            {isDateTimeField
                                ? formatDateTime(data[field], customFieldObject?.dataType)
                                : fieldValue}
                        </div>
                    </div>
                );
            }
        }
    };

    return (
        <div className={classes.main}>
            <Collapse
                defaultActiveKey={sections?.map((section: any) => section.prettyName)}
                expandIconPosition="end"
                className={classes.summaryCardCollapse}
                ghost
            >
                {sections?.map((section: any) => {
                    const isLastSection = section.prettyName === sections?.[sections.length - 1]?.prettyName;
                    return (
                        <>
                            <Collapse.Panel
                                key={section.prettyName}
                                header={(
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        {section.icon ? <section.icon style={{ fontSize: '20px' }} /> : null}
                                        <span>{section.prettyName}</span>
                                        <EditOutlined
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleTabChange(section.goTo);
                                            }}
                                        />
                                    </div>
                                )}
                            >
                                <div className={classes.fields}>
                                    {
                                        section.fieldsList?.map((field: any) => {
                                            return (
                                                renderField(field)
                                            );
                                        })
                                    }
                                    {
                                        section.fieldsList?.includes('piece') ? (
                                            renderPieces()
                                        ) : null
                                    }
                                    {
                                        section.fieldsList?.includes('invoice') ? (
                                            renderInvoices()
                                        ) : null
                                    }
                                </div>
                            </Collapse.Panel>
                            {!isLastSection && <Divider style={{ margin: '0px' }} />}
                        </>
                    );
                })}
            </Collapse>
        </div>
    );
};

const mapStateToProps = (state: any) => {
    const { master } = state;
    const { parts_to_show, config, properties } = master;
    const show_delivery_type_in_customerportal = get(parts_to_show, 'show_delivery_type_in_customerportal', false);
    const is_cop_applicable = get(parts_to_show, 'is_cop_applicable', false);
    const showEwb = get(config, 'config.customer_portal_config.show_ewb_fields', false);
    const formSubLayout = config?.customer_portal_order_creation_config?.sub_layout || {};
    const pieceLayout = formSubLayout?.piece || [];
    const dgLayout = formSubLayout?.dangerous_good || [];
    const invoiceLayout = formSubLayout?.invoice || [];
    return {
        showDeliveryType: show_delivery_type_in_customerportal,
        isCopApplicable: is_cop_applicable,
        showEwb,
        pickupScheduleCreation: properties?.pickup_schedule_creation,
        pieceLayout,
        dgLayout,
        invoiceLayout,
    };
};


const hocConfig: HocOptions = {
    connectJss: {
        useJss: true,
        styleSheet: summaryStepStyles,
    },
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
};

export default GenericHoc(hocConfig)(SummaryStep);
